import React, {
  useState,
  useRef,
  createContext,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { Box, styled } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { isExternalUrl } from '@leagueplatform/web-common';
import { NavLink } from 'hooks/use-nav-links-list.hook';
import { AppNavLink } from 'components/header-nav/app-nav-link.component';
import { AppBarNavLink } from '@leagueplatform/web-common-components';

// Context for coordinating submenu states across multiple instances
type SubmenuContextType = {
  currentFocusedSubmenu: string | null;
  currentHoveredSubmenu: string | null;
  setFocusedSubmenu: (id: string | null) => void;
  setHoveredSubmenu: (id: string | null) => void;
  clearAllSubmenus: () => void;
};

const SubmenuContext = createContext<SubmenuContextType | undefined>(undefined);

export const SubmenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentFocusedSubmenu, setCurrentFocusedSubmenu] = useState<
    string | null
  >(null);
  const [currentHoveredSubmenu, setCurrentHoveredSubmenu] = useState<
    string | null
  >(null);

  const setFocusedSubmenu = useCallback(
    (id: string | null) => {
      setCurrentFocusedSubmenu(id);
      // When focusing on a submenu, clear any hovered submenu
      if (id && currentHoveredSubmenu && currentHoveredSubmenu !== id) {
        setCurrentHoveredSubmenu(null);
      }
    },
    [currentHoveredSubmenu],
  );

  const setHoveredSubmenu = useCallback(
    (id: string | null) => {
      // Only set hovered if there's no focused submenu, or if it's the same submenu
      if (!currentFocusedSubmenu || currentFocusedSubmenu === id) {
        setCurrentHoveredSubmenu(id);
      }
    },
    [currentFocusedSubmenu],
  );

  const clearAllSubmenus = useCallback(() => {
    setCurrentFocusedSubmenu(null);
    setCurrentHoveredSubmenu(null);
  }, []);

  const value = useMemo(
    () => ({
      currentFocusedSubmenu,
      currentHoveredSubmenu,
      setFocusedSubmenu,
      setHoveredSubmenu,
      clearAllSubmenus,
    }),
    [
      currentFocusedSubmenu,
      currentHoveredSubmenu,
      setFocusedSubmenu,
      setHoveredSubmenu,
      clearAllSubmenus,
    ],
  );

  return (
    <SubmenuContext.Provider value={value}>{children}</SubmenuContext.Provider>
  );
};

const useSubmenuContext = () => {
  const context = useContext(SubmenuContext);
  if (!context) {
    throw new Error('useSubmenuContext must be used within a SubmenuProvider');
  }
  return context;
};

type AppNavSubmenuToggleProps = {
  navLink: NavLink;
  subMenuIsOpen: boolean;
  handleBlur: React.FocusEventHandler<HTMLElement>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleFocus: () => void;
  onNavLinkClick: () => void;
};
type SubmenuDropdownProps = {
  children: React.ReactNode;
  subMenuIsOpen: boolean;
  onBlur: React.FocusEventHandler<HTMLElement>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

/**
 * The button used to toggle the submenu.
 * @param navLink - The nav link to display the button for.
 * @param handleToggleSubmenu - The function to call when the button is clicked.
 * @param subMenuIsOpen - Whether the submenu is open.
 * @param handleBlur - The function to call when the button is blurred.
 * @param handleMouseEnter - The function to call when the mouse enters the button.
 * @param handleMouseLeave - The function to call when the mouse leaves the button.
 * @param handleFocus - The function to call when the button receives focus.
 * @param onNavLinkClick - The function to call when the nav link is clicked for navigation.
 */
const SubmenuToggle = ({
  navLink,
  subMenuIsOpen,
  handleBlur,
  handleMouseEnter,
  handleMouseLeave,
  handleFocus,
  onNavLinkClick,
}: AppNavSubmenuToggleProps) => {
  const { formatMessage } = useIntl();

  return (
    <AppBarNavLink
      to={navLink.to}
      key={navLink.message_id}
      aria-haspopup="dialog"
      aria-expanded={subMenuIsOpen}
      aria-controls={`${navLink.message_id}_menu`}
      color="onSurface.text.primary"
      hoverStyle={{ color: 'interactive.action.primary' }}
      decorativeBarColor="interactive.action.primary"
      display="flex"
      justifyContent="center"
      alignItems="center"
      listItemProps={{
        justifyContent: 'center',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={onNavLinkClick}
    >
      <Box css={{ justifyContent: 'center' }}>
        {formatMessage({ id: navLink.message_id })}
      </Box>
    </AppBarNavLink>
  );
};
/**
 * The dropdown container for the submenu.
 * @param children - The children to display in the dropdown.
 * @param subMenuIsOpen - Whether the submenu is open.
 * @param onBlur - The function to call when the dropdown is blurred.
 * @param onMouseEnter - The function to call when the mouse enters the dropdown.
 * @param onMouseLeave - The function to call when the mouse leaves the dropdown.
 */
const SubmenuDropdown = ({
  children,
  subMenuIsOpen,
  onBlur,
  onMouseEnter,
  onMouseLeave,
}: SubmenuDropdownProps) => (
  <Box
    css={{
      transitionProperty: 'opacity',
      transitionDuration: '250ms',
      animationTimingFunction: 'ease',
      position: 'absolute',
      top: '100%',
      insetInlineStart: '0',
      border: '$thin',
      backgroundColor: '$surfaceBackgroundSecondary',
      boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.25)',
      zIndex: 999,
      minWidth: '200px',
      opacity: subMenuIsOpen ? '1' : '0',
      display: subMenuIsOpen ? 'flex' : 'none',
      gap: '$four',
      padding: '$oneAndHalf $one',
    }}
    onBlur={onBlur}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </Box>
);

export const DropdownColumn = styled(Box, {
  display: 'flex',
  flexDirection: 'column',
});

export const DropdownList = styled(Box, {
  display: 'flex',
  flexDirection: 'column',
  gap: '$oneAndHalf',
});

type DropdownListItemProps = {
  navLink: NavLink;
  onNavLinkClick: () => void;
};

/**
 * The navigation item displayed in the submenu.
 * @param navLink - The navigation link data.
 * @param onNavLinkClick - The function to call when the nav link is clicked.
 */
export const DropdownListItem = ({
  navLink,
  onNavLinkClick,
}: DropdownListItemProps) => {
  const isExternal = isExternalUrl(navLink.to);
  return (
    <Box
      css={{
        color: '$onSurfaceTextPrimary',
        fontSize: '$bodyTwo',
      }}
    >
      {isExternal ? (
        <a
          href={navLink.to as string}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavLinkClick}
        >
          {navLink?.text || navLink.message_id}
        </a>
      ) : (
        <Box onClick={onNavLinkClick}>
          <AppNavLink navLink={navLink} />
        </Box>
      )}
    </Box>
  );
};

export const AppNavSubmenu = ({ navLink }: { navLink: NavLink }) => {
  const submenuRef = useRef<HTMLLIElement>(null);
  const {
    currentFocusedSubmenu,
    currentHoveredSubmenu,
    setFocusedSubmenu,
    setHoveredSubmenu,
    clearAllSubmenus,
  } = useSubmenuContext();

  // Generate unique ID for this submenu
  const submenuId = `submenu-${navLink.message_id}`;

  // Determine if this submenu should be open based on context state
  const subMenuIsOpen =
    currentFocusedSubmenu === submenuId || currentHoveredSubmenu === submenuId;

  const handleMouseEnter = () => {
    setHoveredSubmenu(submenuId);
  };

  const handleMouseLeave = () => {
    // Only clear hover if this submenu is currently hovered (not focused)
    if (currentHoveredSubmenu === submenuId) {
      setHoveredSubmenu(null);
    }
  };

  const handleFocus = () => {
    // Focus always takes priority and clears any hovered submenu
    setFocusedSubmenu(submenuId);
  };

  const handleNavLinkClick = () => {
    // Close all submenus when any navigation link is clicked
    clearAllSubmenus();
  };

  const hasSubMenuItems = Boolean(navLink?.subMenu?.items.length);

  // On blur, close the submenu if it was opened by focus
  const handleBlur: React.FocusEventHandler<HTMLElement> = (event) => {
    // Close the submenu when focus moves outside of the submenu container
    const nextFocused = event.relatedTarget as Node | null;
    if (!nextFocused || !submenuRef.current?.contains(nextFocused)) {
      // Only clear focus if this submenu is currently focused
      if (currentFocusedSubmenu === submenuId) {
        setFocusedSubmenu(null);
      }
    }
  };

  return (
    <Box
      ref={submenuRef}
      as="li"
      css={{
        listStyle: 'none',
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <SubmenuToggle
        handleBlur={handleBlur}
        navLink={navLink}
        subMenuIsOpen={subMenuIsOpen}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        handleFocus={handleFocus}
        onNavLinkClick={handleNavLinkClick}
      />
      <SubmenuDropdown
        onBlur={handleBlur}
        subMenuIsOpen={subMenuIsOpen && hasSubMenuItems}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <DropdownColumn>
          <DropdownList>
            {navLink?.subMenu?.items.map((item) => (
              <DropdownListItem
                key={item.message_id}
                navLink={item}
                onNavLinkClick={handleNavLinkClick}
              />
            ))}
          </DropdownList>
        </DropdownColumn>
      </SubmenuDropdown>
    </Box>
  );
};
